import { Text } from "@/components/ui/baseUi/text"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useMemberSideBar } from "@/modules/members/hooks/useMemberSidebar"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import { CommentEntity } from "@/modules/social/entities/socialEntity"
import { useDeleteComment } from "@/modules/social/hooks/useSocialCommentActions"
import { useToggleCommentReaction } from "@/modules/social/hooks/useSocialReactions"
import { createSocialCommentModel } from "@/modules/social/models/socialCommentModel"
import { formatTextWithMentions } from "@/modules/social/utils/formatTextWithMentions"
import { useConfirmPopup } from "@/modules/ui/components/popup/huConfirmPopup"
import { useUser } from "@/modules/users/hooks/useUser"
import { Heart } from "lucide-react"
import { useCallback, useState } from "react"

export type SocialCommentItemProps = {
  socialPostId: string
  comment: CommentEntity
  onReply: (commentId: string, username?: string) => void
  defaultShowReplies?: boolean
  size?: "sm" | "md"
}

export function SocialCommentItem({
  socialPostId,
  comment,
  onReply,
  defaultShowReplies,
  size = "md",
}: SocialCommentItemProps) {
  const [showReplies, setShowReplies] = useState(defaultShowReplies ?? false)
  const [prevDefaultShowReplies, setPrevDefaultShowReplies] = useState(defaultShowReplies)
  if (defaultShowReplies && !prevDefaultShowReplies) {
    setPrevDefaultShowReplies(defaultShowReplies)
    setShowReplies(true)
  }

  const { addReaction, removeReaction, isPending } = useToggleCommentReaction(socialPostId, comment.id)
  const { mutate: deleteComment } = useDeleteComment(socialPostId)
  const { navigateToMemberDetailsInfo } = useMemberSideBar()
  const navigateToUserProfile = useCallback(
    (userId: string) => {
      navigateToMemberDetailsInfo(userId, {})
    },
    [navigateToMemberDetailsInfo],
  )
  const user = useUser()
  const { show: showConfirmPopup } = useConfirmPopup()

  const model = createSocialCommentModel(comment, user.id)

  const handleToggleHeart = () => {
    if (isPending) return
    if (model.currentUserEmoji === "❤️") {
      removeReaction()
    } else {
      addReaction("❤️")
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-3">
        <ProfileAvatar
          profile={{
            avatar: comment.user?.avatar ?? null,
            username: comment.user?.username ?? word("social.comment.unknownUser"),
          }}
          className={cn("size-8 shrink-0", size === "sm" && "size-6")}
        />
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-1">
            <Text fontStyle="LS" className="text-textOnSurface-default">
              {model.displayName}
            </Text>
            <Text fontStyle="LS" className="text-textOnSurface-muted">
              {model.creationDateLabel}
            </Text>
          </div>

          {comment.text && (
            <Text fontStyle="BS" className="text-textOnSurface-default">
              {formatTextWithMentions(comment.text, comment.mentions, navigateToUserProfile)}
            </Text>
          )}

          <div className="mt-1 flex gap-4">
            <button
              onClick={() => {
                onReply(comment.id, comment.user?.username)
                setShowReplies(true)
              }}
              className="cursor-pointer"
            >
              <Text fontStyle="BS" className="font-semibold text-textOnSurface-disabled">
                {word("social.comment.reply")}
              </Text>
            </button>

            {model.hasReplies && !showReplies && (
              <button
                onClick={() => {
                  setShowReplies(true)
                }}
                className="cursor-pointer"
              >
                <Text fontStyle="BS" className="font-semibold text-textOnSurface-disabled">
                  {word("social.comment.viewReplies")}
                </Text>
              </button>
            )}

            {model.hasReplies && showReplies && (
              <button
                onClick={() => {
                  setShowReplies(false)
                }}
                className="cursor-pointer"
              >
                <Text fontStyle="BS" className="font-semibold text-textOnSurface-disabled">
                  {word("social.comment.hideReplies")}
                </Text>
              </button>
            )}

            {model.canDelete && (
              <button
                onClick={() => {
                  showConfirmPopup({
                    title: word("social.comment.popup.delete.title"),
                    message: word("social.comment.popup.delete.description"),
                    accept: () => {
                      deleteComment(comment.id)
                    },
                    variant: { dialogFooterConfirm: "negativePrimary" },
                  })
                }}
                className="cursor-pointer"
              >
                <Text fontStyle="BS" className="font-semibold text-textOnSurface-disabled">
                  {word("social.comment.delete")}
                </Text>
              </button>
            )}
          </div>
        </div>

        <button
          onClick={handleToggleHeart}
          disabled={isPending}
          className={cn("mt-2 flex flex-col items-center gap-1 text-textOnSurface-muted transition-colors")}
        >
          <Heart
            className={cn(
              "size-4 cursor-pointer text-textOnSurface-subtle transition-colors",
              model.currentUserEmoji === "❤️" && "text-interactive-text-negative-intense",
            )}
            fill={model.currentUserEmoji === "❤️" ? "currentColor" : "none"}
            color="currentColor"
          />
          <Text fontStyle="LS">{model.totalReactions}</Text>
        </button>
      </div>

      {model.hasReplies && showReplies && (
        <SocialCommentReplyList socialPostId={socialPostId} replies={comment.childrenComments} onReply={onReply} />
      )}
    </div>
  )
}

function SocialCommentReplyList({
  socialPostId,
  replies,
  onReply,
}: {
  socialPostId: string
  replies: CommentEntity[]
  onReply: (commentId: string, username?: string) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      {replies.map((reply) => (
        <div key={reply.id} className="mt-2 ml-11 flex flex-col border-l border-interactive-border-neutral-faded pl-1">
          <SocialCommentItem
            socialPostId={socialPostId}
            comment={reply}
            onReply={() => {
              onReply(reply.parentCommentId ?? reply.id, reply.user?.username)
            }}
            size="sm"
          />
        </div>
      ))}
    </div>
  )
}
